import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { getCourseById, createPaycorpPaymentLink, verifyPaycorpPayment, getPaymentStatus, submitPaymentSlip, getUsersCourses, getPaymentsByUserId } from '../../../../../Constants/Api/functions/function';
import { setUsersCources } from '../../../../../Reducers/usersCourcesReducer';
import { UPLOAD_PAYMENT_SLIP_URL } from '../../../../../Constants/Api/azure/azure';
import { Button, Spin, Tabs, Card, Form, Input, Select, Space, Divider, DatePicker, TimePicker, Upload, message, Modal, Radio, Checkbox, Tag, Alert } from 'antd';
import {
    LeftCircleOutlined,
    CreditCardOutlined,
    BankOutlined,
    LockOutlined,
    UserOutlined,
    CalendarOutlined,
    UploadOutlined,
    FileImageOutlined,
    CheckCircleOutlined,
    DownOutlined,
    UpOutlined,
} from '@ant-design/icons';
import history from '../../../../../../history';

import './styles.scss'
import ibasBankDetails from '../../../../../Assets/Images/ibas_bankDetails.png';

import 'react-quill/dist/quill.bubble.css';
import { generateInvoice } from '../../../../../Utils/InvoiceGenerator';

// Card Payment Tab Component
const CardPaymentTab = ({ course, userProfile, isAlumni, targetInstallmentNumber, existingEnrollment }) => {
    const [form] = Form.useForm();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [paymentUrl, setPaymentUrl] = useState('');
    const [selectedInstallment, setSelectedInstallment] = useState(null);
    const [isInstallmentsExpanded, setIsInstallmentsExpanded] = useState(false);
    const [activePaymentId, setActivePaymentId] = useState('');
    const [activeReqId, setActiveReqId] = useState('');
    const [activeOrderId, setActiveOrderId] = useState('');
    const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
    const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [paymentSuccessDetails, setPaymentSuccessDetails] = useState(null);

    const onPaymentSuccess = () => {
        const amountToPay = selectedInstallment?.amount ?? fullPrice;
        const totalInstCount = course?.installments || (activeBreakdown ? activeBreakdown.length : 0);
        setPaymentSuccessDetails({
            paymentId: activePaymentId,
            amount: amountToPay,
            userName: userProfile?.name,
            courseName: course?.name,
            coursePrice: course?.price,
            coursePromoPrice: course?.promoPrice,
            alumniDiscount: course?.alumniDiscount,
            isAlumni: isAlumniPricing,
            paymentMethod: 'Card Gateway',
            paymentStatus: 'Completed',
            date: new Date().toLocaleDateString(),
            planName: selectedInstallment ? 'Installment Plan' : (course?.isPromoActive ? 'Promotional Full Payment' : 'Standard Full Payment'),
            installmentNumber: selectedInstallment ? selectedInstallment.installmentNumber : undefined,
            totalInstallments: totalInstCount,
            installments: activeBreakdown || course?.installmentBreakdown || []
        });
        setIsSuccess(true);
        message.success('Payment completed successfully!');
    };

    // Determine base price considering alumni discount
    let basePrice = course?.price || 0;
    let isAlumniPricing = false;
    if (isAlumni && course?.alumniDiscount) {
        basePrice = Math.max(0, basePrice - course.alumniDiscount);
        isAlumniPricing = true;
    }

    const activeBreakdown = React.useMemo(() => {
        return isAlumniPricing && course?.alumniInstallmentBreakdown?.length > 0 
            ? course.alumniInstallmentBreakdown 
            : course?.installmentBreakdown;
    }, [course, isAlumniPricing]);

    const installmentTotal = React.useMemo(() => {
        if (!activeBreakdown || activeBreakdown.length === 0) return 0;
        return activeBreakdown.reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
    }, [activeBreakdown]);

    // Default to target installment if specified, or Full Payment (null) in Card payment method
    useEffect(() => {
        if (activeBreakdown && activeBreakdown.length > 0 && targetInstallmentNumber) {
            const target = activeBreakdown.find(inst => Number(inst.installmentNumber) === Number(targetInstallmentNumber));
            if (target) {
                setSelectedInstallment(target);
                setIsInstallmentsExpanded(true);
                return;
            }
        }
        setSelectedInstallment(null);
    }, [activeBreakdown, targetInstallmentNumber]);

    // Handle postMessage redirect responses from successful/failed checkout (backup channel)
    useEffect(() => {
        const handlePaymentMessage = (event) => {
            const { data } = event;
            if (data && data.status) {
                if (data.status === 'PAYMENT_SUCCESS') {
                    onPaymentSuccess();
                } else if (data.status === 'PAYMENT_FAILED') {
                    setIsPaymentModalVisible(false);
                    setPaymentUrl('');
                    message.error(data.error || 'Payment failed. Please try again.');
                }
            }
        };

        window.addEventListener('message', handlePaymentMessage);
        return () => {
            window.removeEventListener('message', handlePaymentMessage);
        };
    }, []);

    // Robust Fallback: Poll payment status in our database every 3 seconds while modal is active.
    // This triggers checkout completion even if mixed content blocks redirect pages in development.
    useEffect(() => {
        let intervalId;
        if (isPaymentModalVisible && activePaymentId) {
            intervalId = setInterval(async () => {
                try {
                    const result = await getPaymentStatus(activePaymentId);
                    if (result && result.success) {
                        if (result.status === 'completed') {
                            clearInterval(intervalId);
                            onPaymentSuccess();
                        } else if (result.status === 'failed') {
                            clearInterval(intervalId);
                            setIsPaymentModalVisible(false);
                            setPaymentUrl('');
                            message.error('Payment failed or was canceled. Please try again.');
                        }
                    }
                } catch (error) {
                    console.error('Error during payment status polling:', error);
                }
            }, 3000);
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isPaymentModalVisible, activePaymentId]);

    const handlePaycorpPayment = async () => {
        setIsProcessing(true);
        try {
            // Determine amount: either selected installment or full course price
            const amountToPay = selectedInstallment?.amount ?? fullPrice;

            const totalInstallments = course?.installments || 0;
            const installmentNumber = selectedInstallment
                ? selectedInstallment.installmentNumber
                : 0;

            const paymentData = {
                userId: userProfile?.id,
                courseId: course?.id,
                amount: amountToPay,
                description: selectedInstallment
                    ? `Payment for ${course?.name} - Installment ${selectedInstallment.installmentNumber}`
                    : `Payment for ${course?.name} - Full Payment`,
                installmentNumber,
                totalInstallments
            };

            const result = await createPaycorpPaymentLink(paymentData);

            if (result.success && result.paymentLink) {
                // Save database IDs for status checking and manual verification
                setActivePaymentId(result.paymentId);
                setActiveReqId(result.reqid);
                setActiveOrderId(result.orderId);
                
                // Open payment link in a new browser tab/window
                const newWindow = window.open(result.paymentLink, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    message.warning('Popup blocker detected. Please click the button to complete payment.');
                }
                
                setPaymentUrl(result.paymentLink);
                setIsSuccess(false);
                setPaymentSuccessDetails(null);
                setIsPaymentModalVisible(true);
                message.success('Payment session started. Please complete it in the new tab.');
            } else {
                message.error(result.message || 'Failed to load payment page');
            }
        } catch (error) {
            console.error('Payment error:', error);
            message.error('An error occurred while processing payment');
        }
        setIsProcessing(false);
    };

    const handleManualVerify = async () => {
        if (!activeReqId) return;
        setIsVerifying(true);
        try {
            // First check local status in database (in case callback completed but modal didn't get it)
            const localResult = await getPaymentStatus(activePaymentId);
            if (localResult && localResult.success && localResult.status === 'completed') {
                onPaymentSuccess();
                setIsVerifying(false);
                return;
            }

            // If not marked completed locally yet, call verify to check server-to-server with Paycorp
            const result = await verifyPaycorpPayment(activeReqId, activeOrderId);
            if (result && result.success && result.status === 'SUCCESS') {
                onPaymentSuccess();
            } else {
                message.error(result.message || 'Payment check failed. Please make sure you completed the payment in the other window first.');
            }
        } catch (error) {
            console.error('Manual verification error:', error);
            message.error('Failed to verify payment. Please try again.');
        }
        setIsVerifying(false);
    };

    const handleClosePaymentModal = () => {
        setIsPaymentModalVisible(false);
        setPaymentUrl('');
    };

    const isPromoActive = () => {
        if (!course?.isPromoActive || !course?.promoPrice) return false;
        if (course.promoStartDate && course.promoEndDate) {
            const today = new Date();
            const start = new Date(course.promoStartDate);
            const end = new Date(course.promoEndDate);
            end.setHours(23, 59, 59, 999);
            return today >= start && today <= end;
        }
        return false;
    };

    const activePromo = isPromoActive();
    const fullPrice = activePromo ? course?.promoPrice : basePrice;
    const planName = activePromo ? "Plan 1 - Promotional Full Payment" : (isAlumniPricing ? "Plan 1 - Alumni Full Payment" : "Plan 2 - Full Payment");

    // Amount to display on buttons based on selected option
    const displayAmount = selectedInstallment?.amount ?? fullPrice;

    return (
        <div className="payment-tab card-payment">
            <div className="payment-summary">
                <h3>Payment Summary</h3>
                <div className="summary-item">
                    <span>Course:</span>
                    <span>{course?.name}</span>
                </div>
                <div className="summary-item">
                    <span>Total Course Fee:</span>
                    <span className="amount">
                        {activePromo ? (
                            <>
                                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8, fontSize: '0.85em' }}>
                                    {course?.price} LKR
                                </span>
                                <span style={{ color: '#d32f2f' }}>{fullPrice} LKR (Promo)</span>
                            </>
                        ) : (
                            <>{fullPrice} LKR</>
                        )}
                    </span>
                </div>

                {/* Installment options */}
                {activeBreakdown && activeBreakdown.length > 0 && (
                    <>
                        <Divider />
                        <div className="summary-item">
                            <span>Choose Payment Option:</span>
                        </div>
                        <Radio.Group
                            onChange={(e) => setSelectedInstallment(e.target.value)}
                            value={selectedInstallment}
                            style={{ width: '100%' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Radio value={null} disabled={existingEnrollment && Number(existingEnrollment.payedInstallments) > 0}>
                                    <strong>{planName}: {fullPrice} LKR</strong>
                                    {existingEnrollment && Number(existingEnrollment.payedInstallments) > 0 && (
                                        <span style={{ color: '#888', marginLeft: 8, fontSize: '0.85rem' }}>
                                            (Unavailable - Currently on installment plan)
                                        </span>
                                    )}
                                </Radio>

                                <div
                                    onClick={() => setIsInstallmentsExpanded(!isInstallmentsExpanded)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        padding: '8px 12px',
                                        background: isInstallmentsExpanded ? '#e6f4ff' : '#f5f5f5',
                                        border: isInstallmentsExpanded ? '1px solid #91caff' : '1px solid #d9d9d9',
                                        borderRadius: 6,
                                        marginTop: 10,
                                        marginBottom: 6,
                                        transition: 'all 0.2s ease',
                                        userSelect: 'none',
                                        flexWrap: 'wrap',
                                        gap: 8
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 600, color: '#1f1f1f', fontSize: '0.9rem' }}>
                                            Plan 3 - Installment Plan ({activeBreakdown.length} Installments - Total: {installmentTotal.toLocaleString()} LKR)
                                        </span>
                                        {selectedInstallment && (
                                            <Tag color="blue" style={{ margin: 0, fontSize: '0.75rem', lineHeight: '18px' }}>
                                                Inst. {selectedInstallment.installmentNumber} Selected
                                            </Tag>
                                        )}
                                    </div>
                                    <span style={{ color: '#1677ff', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
                                        {isInstallmentsExpanded ? 'Minimize' : 'Expand Options'}
                                        {isInstallmentsExpanded ? <UpOutlined /> : <DownOutlined />}
                                    </span>
                                </div>

                                {isInstallmentsExpanded && (
                                    <div style={{ paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {activeBreakdown.map((installment, index) => {
                                            const currentPaid = existingEnrollment ? (Number(existingEnrollment.payedInstallments) || 0) : 0;
                                            const instNum = Number(installment.installmentNumber);
                                            const isAlreadyPaid = instNum <= currentPaid;
                                            const isFutureLocked = instNum > currentPaid + 1;
                                            let dueText = 'At registration';
                                            if (installment.dueDate) {
                                                const dueDateObj = new Date(installment.dueDate);
                                                dueText = `Due ${dueDateObj.toLocaleDateString()}`;
                                            }
                                            return (
                                                <Radio key={installment.installmentNumber} value={installment} disabled={isAlreadyPaid || isFutureLocked}>
                                                    Installment {installment.installmentNumber}: {installment.amount} LKR <span style={{ color: '#888', fontSize: '0.9em' }}>- {dueText}</span>
                                                    {isAlreadyPaid && (
                                                        <Tag color="green" style={{ marginLeft: 6, fontSize: '0.75rem', lineHeight: '18px' }}>
                                                            Paid
                                                        </Tag>
                                                    )}
                                                    {isFutureLocked && (
                                                        <span style={{ color: '#aaa', fontSize: '0.8rem', marginLeft: 6 }}>
                                                            (Pay installment {currentPaid + 1} first)
                                                        </span>
                                                    )}
                                                </Radio>
                                            );
                                        })}
                                        <div style={{
                                            marginTop: 6,
                                            paddingTop: 8,
                                            borderTop: '1px dashed #d9d9d9',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            fontSize: '0.9rem',
                                            color: '#333'
                                        }}>
                                            <span>Total of Installments:</span>
                                            <strong style={{ color: '#1677ff' }}>{installmentTotal.toLocaleString()} LKR</strong>
                                        </div>
                                    </div>
                                )}
                            </Space>
                        </Radio.Group>
                    </>
                )}

                <Divider />
            </div>

            {/* Paycorp Payment Button */}
            <div className="directpay-payment-section">
                <h4><CreditCardOutlined /> Pay with Visa / Mastercard</h4>
                <p style={{ marginBottom: 16, color: '#666' }}>
                    Click the button below to complete your payment securely via our card gateway.
                </p>
                
                <Button
                    type="primary"
                    size="large"
                    loading={isProcessing}
                    block
                    onClick={() => setIsTermsModalVisible(true)}
                    className="pay-button"
                    icon={<CreditCardOutlined />}
                >
                    {isProcessing
                        ? 'Processing Payment...'
                        : `Pay ${displayAmount} LKR with Card`}
                </Button>

                <p style={{ marginTop: 12, textAlign: 'center', fontSize: '13px', color: '#666' }}>
                    You will be prompted to accept the Payment Policy and Terms & Conditions before being redirected to the payment gateway.
                </p>

                <div className="security-notice" style={{ marginTop: 16, textAlign: 'center' }}>
                    <LockOutlined /> Your payment information is secure and encrypted
                </div>
            </div>

            {/* Terms and Conditions Modal */}
            <Modal
                open={isTermsModalVisible}
                onCancel={() => {
                    setIsTermsModalVisible(false);
                    setHasAgreedToTerms(false);
                }}
                footer={null}
                width={700}
                destroyOnClose
                centered
                title={<h3 style={{ margin: 0, fontSize: '20px', color: '#03245e' }}>Payment Policy and Terms & Conditions</h3>}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Effective Date: 04 July 2026</p>
                    <div className="terms-modal-content" style={{ 
                        maxHeight: '350px', 
                        overflowY: 'auto', 
                        padding: '16px', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '8px',
                        backgroundColor: '#f7fafc',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        color: '#4a5568'
                    }}>
                        <p style={{ fontWeight: '500' }}>By registering for any programme, making a payment, or accessing the IBAS online learning platform, website, Learning Management System (LMS), mobile platform, or any educational resources provided by the Institute of Business and Accounting Studies (IBAS), you acknowledge that you have read, understood, and agreed to be bound by the following Payment Policy and Terms & Conditions.</p>
                        
                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>1. Student Registration</h4>
                        <p>1.1 Every student must complete the official registration process by providing accurate, complete, and up-to-date information.</p>
                        <p>1.2 IBAS reserves the right to reject, suspend, or cancel any registration where false, misleading, or incomplete information has been provided.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>2. Course Fees and Payments</h4>
                        <p>2.1 All course fees must be paid according to the payment plan specified by IBAS.</p>
                        <p>2.2 Students are responsible for making all payments on or before the due dates.</p>
                        <p>2.3 Access to classes, recorded lectures, study materials, examinations, certificates, or any other services may be suspended until outstanding payments are settled.</p>
                        <p>2.4 Course fees are valid only for the specific programme and intake (batch) for which payment has been made.</p>
                        <p>2.5 Payments cannot be transferred to another course, programme, batch, or another student unless approved in writing by the Management of IBAS.</p>

                        <h4 style={{ color: '#c05621', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #feebc8', paddingBottom: '4px' }}>3. Refund Policy</h4>
                        <p style={{ fontWeight: 'bold', color: '#c05621' }}>3.1 All payments made to IBAS are strictly non-refundable.</p>
                        <p>3.2 No refunds will be issued under any circumstances, including but not limited to:</p>
                        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                            <li>Change of mind</li>
                            <li>Incorrect course selection by the student</li>
                            <li>Failure to attend classes</li>
                            <li>Failure to complete the programme</li>
                            <li>Personal, financial, employment, or family reasons</li>
                            <li>Visa-related matters</li>
                            <li>Medical reasons</li>
                            <li>Lack of internet access or technical facilities</li>
                            <li>Failure to meet course requirements</li>
                            <li>Disciplinary suspension or expulsion</li>
                        </ul>
                        <p>3.3 Once a payment has been processed, it cannot be cancelled or reversed.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>4. Payment Confirmation</h4>
                        <p>4.1 Payments made via online payment gateways are normally confirmed automatically.</p>
                        <p>4.2 Payments made through bank deposits or bank transfers may require up to three (3) working days for verification.</p>
                        <p>4.3 Students must include their Student ID, NIC number, or the payment reference when making bank deposits or transfers.</p>
                        <p>4.4 Failure to provide accurate payment details may delay access to the course.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>5. Discounts and Promotional Offers</h4>
                        <p>5.1 Discounts are available only during officially announced promotional periods.</p>
                        <p>5.2 Promotional discounts cannot be claimed after the promotion has expired.</p>
                        <p>5.3 IBAS reserves the right to request supporting documents before granting any special discount.</p>
                        <p>5.4 If requested documents are not submitted, IBAS may cancel the discount and request payment of the remaining balance.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>6. Access to Online Learning Platform</h4>
                        <p>6.1 Access is granted only after the required payment has been received and verified.</p>
                        <p>6.2 Access is provided exclusively to the registered student.</p>
                        <p>6.3 Student accounts are strictly personal and must not be shared.</p>
                        <p>6.4 One account may only be used by one individual.</p>
                        <p>6.5 Sharing usernames or passwords is strictly prohibited.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>7. Device Usage</h4>
                        <p>7.1 Students may access the system using one computer and one mobile device.</p>
                        <p>7.2 Simultaneous access from multiple devices is not permitted.</p>
                        <p>7.3 If a device is replaced or damaged, students must contact IBAS to request a device reset.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>8. Recorded Lectures</h4>
                        <p>8.1 Recorded lectures are normally uploaded within 24 to 48 hours after the live session, subject to technical and operational requirements.</p>
                        <p>8.2 Upload times are estimates only and may vary.</p>
                        <p>8.3 Access to recorded lectures will expire according to the validity period of the enrolled batch.</p>
                        <p>8.4 Students wishing to access materials for a future intake must register again and pay the applicable course fee.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>9. Attendance Requirements</h4>
                        <p>9.1 Students are expected to attend all scheduled live sessions.</p>
                        <p>9.2 Attendance requirements vary according to each programme.</p>
                        <p>9.3 Failure to meet the minimum attendance requirements may affect eligibility for examinations, certificates, or course completion.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>10. Intellectual Property</h4>
                        <p>10.1 All course materials, lecture recordings, videos, notes, presentations, examinations, assignments, and learning resources are the exclusive intellectual property of the Institute of Business and Accounting Studies (IBAS).</p>
                        <p>10.2 Students shall not:</p>
                        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                            <li>Download protected videos</li>
                            <li>Record lectures</li>
                            <li>Copy course materials</li>
                            <li>Share learning resources</li>
                            <li>Upload materials to social media</li>
                            <li>Distribute content to third parties</li>
                            <li>Sell or reproduce any IBAS content</li>
                        </ul>
                        <p>10.3 Any violation may result in immediate suspension, permanent termination of access, legal action, and claims for damages and compensation.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>11. Misuse of Student Accounts</h4>
                        <p>IBAS reserves the right to immediately suspend or permanently terminate any account where there is evidence of account sharing, password sharing, multiple users accessing one account, unauthorized downloads, screen recording, copying videos, unauthorized distribution of materials, or any activity considered fraudulent or harmful to IBAS. No refund will be issued following such action.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>12. Technical Requirements</h4>
                        <p>Students are responsible for ensuring they have a reliable internet connection, compatible computer/mobile device, adequate data connectivity, and updated web browsers. IBAS is not responsible for interruptions caused by power failures, internet outages, device failures, network congestion, software issues, or student-side technical problems.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>13. Communication</h4>
                        <p>Official communication will be made through email, WhatsApp Community, Student Portal, official website, or LMS. Students are responsible for regularly checking official communication channels.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>14. Student Conduct</h4>
                        <p>Students are expected to maintain professional and respectful behaviour. Disciplinary action may be taken for harassment, abuse of staff or lecturers, offensive language, threatening behaviour, disruptive conduct, academic misconduct, or breach of institute policies. No refund shall be payable following disciplinary action.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>15. Course Schedule</h4>
                        <p>IBAS reserves the right to modify lecture dates, times, schedules, lecturers, examination dates, or delivery methods due to operational, academic, technical, or unforeseen circumstances.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>16. Privacy and Data Protection</h4>
                        <p>IBAS collects and stores personal information (Full Name, NIC/Passport, DOB, Email, Phone, Course Info, Payment Records, Exam Results) in accordance with data protection laws. Information will not be disclosed to third parties without consent, except where required by law or for academic/administrative purposes.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>17. Student Achievements</h4>
                        <p>Students who receive awards or academic recognition may have their names, photos, testimonials, or achievements published by IBAS for educational, promotional, or marketing purposes unless prohibited by law.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>18. Copyright</h4>
                        <p>All educational materials provided by IBAS are protected by copyright. Any unauthorized reproduction, copying, distribution, publication, or commercial use is strictly prohibited.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>19. Limitation of Liability</h4>
                        <p>IBAS shall not be liable for direct, indirect, incidental, or consequential loss arising from technical failures, internet interruptions, delayed access, device incompatibility, force majeure events, student negligence, or third-party service failures.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>20. Complaints and Disputes</h4>
                        <p>Students must submit complaints or appeals through official communication channels. IBAS will investigate and respond within a reasonable period.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>21. Governing Law</h4>
                        <p>These Terms & Conditions shall be governed by and interpreted in accordance with the laws of the Democratic Socialist Republic of Sri Lanka. Any disputes shall be subject to the exclusive jurisdiction of the competent courts of Sri Lanka.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>22. Acceptance</h4>
                        <p>By registering, making payment, accessing the IBAS website, LMS, or attending any programme offered by IBAS, the student confirms that they have read, understood, and accepted these Payment Policy and Terms & Conditions in full. Failure to comply may result in suspension or termination of services without refund.</p>
                    </div>

                    <div style={{ padding: '0 4px', marginTop: '8px' }}>
                        <Checkbox 
                            checked={hasAgreedToTerms} 
                            onChange={(e) => setHasAgreedToTerms(e.target.checked)}
                            style={{ display: 'flex', alignItems: 'flex-start' }}
                        >
                            <span style={{ fontSize: '13px', lineHeight: '1.4', display: 'inline-block' }}>
                                I have read, understood, and agree to be bound by the <strong>Payment Policy and Terms & Conditions</strong> in full.
                            </span>
                        </Checkbox>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                        <Button 
                            onClick={() => {
                                setIsTermsModalVisible(false);
                                setHasAgreedToTerms(false);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            disabled={!hasAgreedToTerms}
                            onClick={() => {
                                setIsTermsModalVisible(false);
                                handlePaycorpPayment();
                            }}
                            style={{ 
                                fontWeight: 500
                            }}
                        >
                            Agree and Proceed
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Paycorp payment status modal */}
            <Modal
                open={isPaymentModalVisible}
                onCancel={() => {
                    if (isSuccess) {
                        window.location.href = '/dashboard/mycources';
                    } else {
                        handleClosePaymentModal();
                    }
                }}
                footer={null}
                width={500}
                destroyOnClose
                centered
                title={isSuccess ? "Payment Successful" : "Card Payment In Progress"}
                bodyStyle={{ padding: '32px 24px', textAlign: 'center' }}
            >
                {isSuccess ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                        <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                        <h3 style={{ margin: '12px 0 4px', fontSize: 18, color: '#32325d' }}>
                            Payment Completed Successfully!
                        </h3>
                        <p style={{ color: '#6b7c93', fontSize: 14, lineHeight: '1.5', margin: 0 }}>
                            Thank you for your purchase. You can now download your invoice or go to your courses.
                        </p>
                        <div style={{ marginTop: 12, width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <Button 
                                type="primary" 
                                onClick={() => generateInvoice(paymentSuccessDetails)}
                                style={{ height: 40, borderRadius: 6, fontWeight: 500 }}
                                block
                            >
                                Download Invoice
                            </Button>
                            <Button 
                                type="default" 
                                onClick={() => window.location.href = '/dashboard/mycources'}
                                style={{ height: 40, borderRadius: 6, fontWeight: 500 }}
                                block
                            >
                                Go to My Courses
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                        <Spin size="large" />
                        <h3 style={{ margin: '12px 0 4px', fontSize: 18, color: '#32325d' }}>
                            Completing Checkout
                        </h3>
                        <p style={{ color: '#6b7c93', fontSize: 14, lineHeight: '1.5', margin: 0 }}>
                            We have opened the secure Card payment page in a new window/tab. 
                            Please enter your card details and OTP there to complete the purchase.
                        </p>
                    <div style={{ marginTop: 12, width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Button 
                            type="primary" 
                            onClick={handleManualVerify}
                            loading={isVerifying}
                            style={{ height: 40, borderRadius: 6, fontWeight: 500, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            block
                        >
                            Verify Payment Status
                        </Button>
                        {paymentUrl && (
                            <Button 
                                type="default" 
                                href={paymentUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ height: 40, borderRadius: 6, fontWeight: 500 }}
                                block
                            >
                                Open Payment Window Again
                            </Button>
                        )}
                    </div>
                    <span style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                        🔒 Secure encrypted checkout. This page will automatically update once payment is confirmed.
                    </span>
                </div>
                )}
            </Modal>
        </div>
    );
};

// Bank Deposit Tab Component
const BankDepositTab = ({ course, userProfile, isAlumni, targetInstallmentNumber, existingEnrollment, pendingSlip }) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [selectedInstallment, setSelectedInstallment] = useState(null);
    const [isInstallmentsExpanded, setIsInstallmentsExpanded] = useState(false);
    const [isTermsVisible, setIsTermsVisible] = useState(false);

    // Determine base price considering alumni discount
    let basePrice = course?.price || 0;
    let isAlumniPricing = false;
    if (isAlumni && course?.alumniDiscount) {
        basePrice = Math.max(0, basePrice - course.alumniDiscount);
        isAlumniPricing = true;
    }

    const isPromoActive = () => {
        if (!course?.isPromoActive || !course?.promoPrice) return false;
        if (course.promoStartDate && course.promoEndDate) {
            const today = new Date();
            const start = new Date(course.promoStartDate);
            const end = new Date(course.promoEndDate);
            end.setHours(23, 59, 59, 999);
            return today >= start && today <= end;
        }
        return false;
    };

    const activePromo = isPromoActive();
    const fullPrice = activePromo ? course?.promoPrice : basePrice;
    const planName = activePromo ? "Plan 1 - Promotional Full Payment" : (isAlumniPricing ? "Plan 1 - Alumni Full Payment" : "Plan 2 - Full Payment");

    const activeBreakdown = React.useMemo(() => {
        return isAlumniPricing && course?.alumniInstallmentBreakdown?.length > 0 
            ? course.alumniInstallmentBreakdown 
            : course?.installmentBreakdown;
    }, [course, isAlumniPricing]);

    const installmentTotal = React.useMemo(() => {
        if (!activeBreakdown || activeBreakdown.length === 0) return 0;
        return activeBreakdown.reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
    }, [activeBreakdown]);

    // Default to target installment if specified, or Full Payment (null) in Bank Deposit method
    useEffect(() => {
        if (activeBreakdown && activeBreakdown.length > 0 && targetInstallmentNumber) {
            const target = activeBreakdown.find(inst => Number(inst.installmentNumber) === Number(targetInstallmentNumber));
            if (target) {
                setSelectedInstallment(target);
                setIsInstallmentsExpanded(true);
                return;
            }
        }
        setSelectedInstallment(null);
    }, [activeBreakdown, targetInstallmentNumber]);

    const handleSubmitPaymentDetails = async (values) => {
        if (!uploadedFile) {
            message.error('Please upload your payment receipt slip.');
            return;
        }

        if (pendingSlip) {
            message.warning('You already have a payment slip under review for this course.');
            return;
        }

        setIsSubmitting(true);
        try {
            // 1. Upload receipt to get slipUrl (append fileName before file so multer captures it)
            const formData = new FormData();
            formData.append('fileName', `slip_${userProfile?.id || 'user'}_${Date.now()}`);
            formData.append('file', uploadedFile);

            const uploadResponse = await fetch(UPLOAD_PAYMENT_SLIP_URL, {
                method: 'POST',
                body: formData,
            });
            const uploadData = await uploadResponse.json();

            if (!uploadData.success) {
                throw new Error(uploadData.message || 'Failed to upload receipt slip');
            }

            // Determine amount and installments count
            const totalCourseInstallments = course?.installments || activeBreakdown?.length || 1;
            const amountToPay = selectedInstallment?.amount ?? fullPrice;
            const payedInstallments = selectedInstallment 
                ? selectedInstallment.installmentNumber 
                : totalCourseInstallments;

            // 2. Submit payment slip details to backend
            const submitData = {
                userId: userProfile?.id,
                courseId: course?.id,
                amount: amountToPay,
                transactionId: values.transactionId,
                paymentDate: values.paymentDate ? values.paymentDate.format('YYYY-MM-DD') : null,
                paymentTime: values.paymentTime ? values.paymentTime.format('HH:mm') : null,
                remark: values.remark,
                slipUrl: uploadData.url,
                payedInstallments: payedInstallments
            };

            const result = await submitPaymentSlip(submitData);

            if (result.success) {
                message.success('Payment details and slip uploaded successfully! Admins will verify your payment and enroll you.');
                form.resetFields();
                setUploadedFile(null);
                setTimeout(() => {
                    window.location.href = '/dashboard/payments';
                }, 1500);
            } else {
                message.error(result.message || 'Failed to submit payment details');
            }
        } catch (error) {
            console.error('Error submitting payment details:', error);
            message.error(error.message || 'Failed to submit payment details. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const uploadProps = {
        name: 'receipt',
        listType: 'picture-card',
        className: 'receipt-uploader',
        maxCount: 1,
        accept: '.jpg,.jpeg,.png,.pdf',
        beforeUpload: (file) => {
            const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type);
            const isValidSize = file.size / 1024 / 1024 < 5; // 5MB

            if (!isValidType) {
                message.error('You can only upload JPG/PNG/PDF files!');
                return false;
            }
            if (!isValidSize) {
                message.error('File must be smaller than 5MB!');
                return false;
            }

            setUploadedFile(file);
            return false; // Prevent auto upload
        },
        onRemove: () => {
            setUploadedFile(null);
        },
    };

    const displayAmount = selectedInstallment?.amount ?? fullPrice;

    return (
        <div className="payment-tab bank-deposit">
            {pendingSlip && (
                <Alert
                    message="Payment Slip Under Verification"
                    description={
                        <div>
                            <p style={{ margin: 0 }}>
                                You have already submitted a bank deposit slip for this course (Amount: <strong>{pendingSlip.amount} LKR</strong>{pendingSlip.directPayTransactionId ? `, Ref: ${pendingSlip.directPayTransactionId}` : ''}) on {pendingSlip.paymentDate || 'recently'}.
                            </p>
                            <p style={{ margin: '6px 0 0 0', color: '#666' }}>
                                Our administration is currently reviewing your payment. Once verified, your course access will be activated.
                            </p>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 20, borderRadius: 8 }}
                />
            )}

            <div className="payment-summary">
                <h3>Payment Summary</h3>
                <div className="summary-item">
                    <span>Course:</span>
                    <span>{course?.name}</span>
                </div>
                <div className="summary-item">
                    <span>Total Course Fee:</span>
                    <span className="amount">
                        {activePromo ? (
                            <>
                                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8, fontSize: '0.85em' }}>
                                    {course?.price} LKR
                                </span>
                                <span style={{ color: '#d32f2f' }}>{fullPrice} LKR (Promo)</span>
                            </>
                        ) : (
                            <>{fullPrice} LKR</>
                        )}
                    </span>
                </div>

                {/* Installment options */}
                {activeBreakdown && activeBreakdown.length > 0 && (
                    <>
                        <Divider />
                        <div className="summary-item">
                            <span>Choose Payment Option:</span>
                        </div>
                        <Radio.Group
                            onChange={(e) => setSelectedInstallment(e.target.value)}
                            value={selectedInstallment}
                            style={{ width: '100%' }}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Radio value={null} disabled={existingEnrollment && Number(existingEnrollment.payedInstallments) > 0}>
                                    <strong>{planName}: {fullPrice} LKR</strong>
                                    {existingEnrollment && Number(existingEnrollment.payedInstallments) > 0 && (
                                        <span style={{ color: '#888', marginLeft: 8, fontSize: '0.85rem' }}>
                                            (Unavailable - Currently on installment plan)
                                        </span>
                                    )}
                                </Radio>

                                <div
                                    onClick={() => setIsInstallmentsExpanded(!isInstallmentsExpanded)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        padding: '8px 12px',
                                        background: isInstallmentsExpanded ? '#e6f4ff' : '#f5f5f5',
                                        border: isInstallmentsExpanded ? '1px solid #91caff' : '1px solid #d9d9d9',
                                        borderRadius: 6,
                                        marginTop: 10,
                                        marginBottom: 6,
                                        transition: 'all 0.2s ease',
                                        userSelect: 'none',
                                        flexWrap: 'wrap',
                                        gap: 8
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 600, color: '#1f1f1f', fontSize: '0.9rem' }}>
                                            Plan 3 - Installment Plan ({activeBreakdown.length} Installments - Total: {installmentTotal.toLocaleString()} LKR)
                                        </span>
                                        {selectedInstallment && (
                                            <Tag color="blue" style={{ margin: 0, fontSize: '0.75rem', lineHeight: '18px' }}>
                                                Inst. {selectedInstallment.installmentNumber} Selected
                                            </Tag>
                                        )}
                                    </div>
                                    <span style={{ color: '#1677ff', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
                                        {isInstallmentsExpanded ? 'Minimize' : 'Expand Options'}
                                        {isInstallmentsExpanded ? <UpOutlined /> : <DownOutlined />}
                                    </span>
                                </div>

                                {isInstallmentsExpanded && (
                                    <div style={{ paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {activeBreakdown.map((installment) => {
                                            const currentPaid = existingEnrollment ? (Number(existingEnrollment.payedInstallments) || 0) : 0;
                                            const instNum = Number(installment.installmentNumber);
                                            const isAlreadyPaid = instNum <= currentPaid;
                                            const isFutureLocked = instNum > currentPaid + 1;
                                            let dueText = 'At registration';
                                            if (installment.dueDate) {
                                                const dueDateObj = new Date(installment.dueDate);
                                                dueText = `Due ${dueDateObj.toLocaleDateString()}`;
                                            }
                                            return (
                                                <Radio key={installment.installmentNumber} value={installment} disabled={isAlreadyPaid || isFutureLocked}>
                                                    Installment {installment.installmentNumber}: {installment.amount} LKR <span style={{ color: '#888', fontSize: '0.9em' }}>- {dueText}</span>
                                                    {isAlreadyPaid && (
                                                        <Tag color="green" style={{ marginLeft: 6, fontSize: '0.75rem', lineHeight: '18px' }}>
                                                            Paid
                                                        </Tag>
                                                    )}
                                                    {isFutureLocked && (
                                                        <span style={{ color: '#aaa', fontSize: '0.8rem', marginLeft: 6 }}>
                                                            (Pay installment {currentPaid + 1} first)
                                                        </span>
                                                    )}
                                                </Radio>
                                            );
                                        })}
                                        <div style={{
                                            marginTop: 6,
                                            paddingTop: 8,
                                            borderTop: '1px dashed #d9d9d9',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            fontSize: '0.9rem',
                                            color: '#333'
                                        }}>
                                            <span>Total of Installments:</span>
                                            <strong style={{ color: '#1677ff' }}>{installmentTotal.toLocaleString()} LKR</strong>
                                        </div>
                                    </div>
                                )}
                            </Space>
                        </Radio.Group>
                    </>
                )}

                <Divider />
                <div className="summary-item">
                    <span>Amount to Deposit:</span>
                    <span className="amount">{displayAmount} LKR</span>
                </div>
                <Divider />
            </div>

            <div className="bank-instructions">
                <h4><BankOutlined /> Bank Transfer Instructions</h4>
                <p>Please follow the following steps to complete your payment:</p>

                <div className="bank-details" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <h5 style={{ alignSelf: 'flex-start', margin: 0 }}>Bank Account Details:</h5>
                    <img 
                        src={ibasBankDetails} 
                        alt="Bank Account Details" 
                        className="bank-details-img"
                        style={{ maxWidth: '100%', height: 'auto', borderRadius: '6px', marginTop: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} 
                    />
                </div>

                <div className="important-notes">
                    <h5>Important Notes:</h5>
                    <ul>
                        <li>Please include your name and course name in the transaction description</li>
                        <li>Keep the transaction receipt safe for verification</li>
                        <li>Processing may take 1-2 business days</li>
                    </ul>
                </div>
            </div>

            
            <div className="alternative-contact">
                <Divider />
                <h5>Alternative: Send Payment Confirmation</h5>
                <p>If you prefer to send the details manually, you can also contact us through:</p>

                <Space direction="vertical" style={{ width: '100%' }}>
                    <div className="contact-item">
                        <span>Coordinator Name: </span>
                        <strong>{course?.coordinatorName || 'Coordinator'}</strong>
                    </div>

                    <div className="contact-item">
                        <span>WhatsApp: </span>
                        <a href={`https://wa.me/${(course?.coordinatorWhatsApp || '+94774747920').replace(/\D/g, '')}?text=Hello%2C%20I%20am%20${encodeURIComponent(userProfile?.name || '')}%20(Index%20No:%20${encodeURIComponent(userProfile?.indexNumber || '')}).%20I%20have%20made%20the%20payment%20for%20the%20course%20${encodeURIComponent(course?.name || '')}%20and%20here%20is%20the%20transaction%20receipt.`} target='_blank' rel="noopener noreferrer">
                            {course?.coordinatorWhatsApp || '+94 77 474 7920'}
                        </a>
                        <Button
                            type='link'
                            size="small"
                            style={{ padding: 0, marginLeft: 10 }}
                            href={`https://wa.me/${(course?.coordinatorWhatsApp || '+94774747920').replace(/\D/g, '')}?text=Hello%2C%20I%20am%20${encodeURIComponent(userProfile?.name || '')}%20(Index%20No:%20${encodeURIComponent(userProfile?.indexNumber || '')}).%20I%20have%20made%20the%20payment%20for%20the%20course%20${encodeURIComponent(course?.name || '')}%20and%20here%20is%20the%20transaction%20receipt.`}
                            target='_blank'
                            rel="noopener noreferrer"
                        >
                            Send WhatsApp
                        </Button>
                    </div>
                </Space>
            </div>

            <Divider />

            <div className="payment-details-form">
                <h4><FileImageOutlined /> Submit Payment Details</h4>
                <p>After making the bank transfer, please fill in the details below and upload your receipt:</p>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmitPaymentDetails}
                    className="payment-form"
                >
                    <div className="form-grid">
                        <Form.Item
                            label="Course Name"
                            name="courseName"
                            initialValue={course?.name}
                            rules={[{ required: true, message: 'Course name is required' }]}
                        >
                            <Input disabled />
                        </Form.Item>

                        <Form.Item
                            label="Reference/Transaction ID"
                            name="transactionId"
                            rules={[{ required: true, message: 'Transaction ID is required' }]}
                        >
                            <Input placeholder="Enter transaction/reference number" />
                        </Form.Item>

                        <Form.Item
                            label="Date"
                            name="paymentDate"
                            rules={[{ required: true, message: 'Payment date is required' }]}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Select payment date"
                                format="YYYY-MM-DD"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Time"
                            name="paymentTime"
                            rules={[{ required: true, message: 'Payment time is required' }]}
                        >
                            <TimePicker
                                style={{ width: '100%' }}
                                placeholder="Select payment time"
                                format="HH:mm"
                                use12Hours={false}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Remark/Description"
                        name="remark"
                        rules={[{ required: true, message: 'Remark is required' }]}
                    >
                        <Input.TextArea
                            placeholder="Enter any additional remarks or description"
                            rows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Upload Receipt"
                        name="receipt"
                    >
                        <Upload {...uploadProps}>
                            {uploadedFile ? null : (
                                <div className="upload-area">
                                    <UploadOutlined />
                                    <div style={{ marginTop: 8 }}>
                                        Click to upload receipt
                                        <br />
                                    </div>
                                </div>
                            )}
                        </Upload>
                        
                    <small className='support-text'>Support: JPG, PNG, PDF (Max: 5MB)</small>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 8 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            loading={isSubmitting}
                            disabled={!!pendingSlip}
                            block
                            className="submit-button"
                        >
                            {isSubmitting ? 'Submitting...' : (pendingSlip ? 'Payment Slip Under Review' : 'Submit Payment Details')}
                        </Button>
                    </Form.Item>
                    <p className="policy-agreement-text" style={{ textAlign: 'center', fontSize: '13px', color: '#666', marginBottom: 20 }}>
                        By submitting, you agree to our{' '}
                        <Button 
                            type="link" 
                            onClick={() => setIsTermsVisible(true)}
                            style={{ padding: 0, height: 'auto', textDecoration: 'underline', fontSize: '13px', color: '#1890ff', verticalAlign: 'baseline' }}
                        >
                            Payment Policy and Terms & Conditions
                        </Button>.
                    </p>
                </Form>
            </div>

            {/* Terms and Conditions Modal */}
            <Modal
                open={isTermsVisible}
                onCancel={() => setIsTermsVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setIsTermsVisible(false)}>
                        Close
                    </Button>
                ]}
                width={700}
                destroyOnClose
                centered
                title={<h3 style={{ margin: 0, fontSize: '20px', color: '#03245e' }}>Payment Policy and Terms & Conditions</h3>}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>Effective Date: 04 July 2026</p>
                    <div className="terms-modal-content" style={{ 
                        maxHeight: '350px', 
                        overflowY: 'auto', 
                        padding: '16px', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '8px',
                        backgroundColor: '#f7fafc',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        color: '#4a5568'
                    }}>
                        <p style={{ fontWeight: '500' }}>By registering for any programme, making a payment, or accessing the IBAS online learning platform, website, Learning Management System (LMS), mobile platform, or any educational resources provided by the Institute of Business and Accounting Studies (IBAS), you acknowledge that you have read, understood, and agreed to be bound by the following Payment Policy and Terms & Conditions.</p>
                        
                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>1. Student Registration</h4>
                        <p>1.1 Every student must complete the official registration process by providing accurate, complete, and up-to-date information.</p>
                        <p>1.2 IBAS reserves the right to reject, suspend, or cancel any registration where false, misleading, or incomplete information has been provided.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>2. Course Fees and Payments</h4>
                        <p>2.1 All course fees must be paid according to the payment plan specified by IBAS.</p>
                        <p>2.2 Students are responsible for making all payments on or before the due dates.</p>
                        <p>2.3 Access to classes, recorded lectures, study materials, examinations, certificates, or any other services may be suspended until outstanding payments are settled.</p>
                        <p>2.4 Course fees are valid only for the specific programme and intake (batch) for which payment has been made.</p>
                        <p>2.5 Payments cannot be transferred to another course, programme, batch, or another student unless approved in writing by the Management of IBAS.</p>

                        <h4 style={{ color: '#c05621', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #feebc8', paddingBottom: '4px' }}>3. Refund Policy</h4>
                        <p style={{ fontWeight: 'bold', color: '#c05621' }}>3.1 All payments made to IBAS are strictly non-refundable.</p>
                        <p>3.2 No refunds will be issued under any circumstances, including but not limited to:</p>
                        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                            <li>Change of mind</li>
                            <li>Incorrect course selection by the student</li>
                            <li>Failure to attend classes</li>
                            <li>Failure to complete the programme</li>
                            <li>Personal, financial, employment, or family reasons</li>
                            <li>Visa-related matters</li>
                            <li>Medical reasons</li>
                            <li>Lack of internet access or technical facilities</li>
                            <li>Failure to meet course requirements</li>
                            <li>Disciplinary suspension or expulsion</li>
                        </ul>
                        <p>3.3 Once a payment has been processed, it cannot be cancelled or reversed.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>4. Payment Confirmation</h4>
                        <p>4.1 Payments made via online payment gateways are normally confirmed automatically.</p>
                        <p>4.2 Payments made through bank deposits or bank transfers may require up to three (3) working days for verification.</p>
                        <p>4.3 Students must include their Student ID, NIC number, or the payment reference when making bank deposits or transfers.</p>
                        <p>4.4 Failure to provide accurate payment details may delay access to the course.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>5. Discounts and Promotional Offers</h4>
                        <p>5.1 Discounts are available only during officially announced promotional periods.</p>
                        <p>5.2 Promotional discounts cannot be claimed after the promotion has expired.</p>
                        <p>5.3 IBAS reserves the right to request supporting documents before granting any special discount.</p>
                        <p>5.4 If requested documents are not submitted, IBAS may cancel the discount and request payment of the remaining balance.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>6. Access to Online Learning Platform</h4>
                        <p>6.1 Access is granted only after the required payment has been received and verified.</p>
                        <p>6.2 Access is provided exclusively to the registered student.</p>
                        <p>6.3 Student accounts are strictly personal and must not be shared.</p>
                        <p>6.4 One account may only be used by one individual.</p>
                        <p>6.5 Sharing usernames or passwords is strictly prohibited.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>7. Device Usage</h4>
                        <p>7.1 Students may access the system using one computer and one mobile device.</p>
                        <p>7.2 Simultaneous access from multiple devices is not permitted.</p>
                        <p>7.3 If a device is replaced or damaged, students must contact IBAS to request a device reset.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>8. Recorded Lectures</h4>
                        <p>8.1 Recorded lectures are normally uploaded within 24 to 48 hours after the live session, subject to technical and operational requirements.</p>
                        <p>8.2 Upload times are estimates only and may vary.</p>
                        <p>8.3 Access to recorded lectures will expire according to the validity period of the enrolled batch.</p>
                        <p>8.4 Students wishing to access materials for a future intake must register again and pay the applicable course fee.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>9. Attendance Requirements</h4>
                        <p>9.1 Students are expected to attend all scheduled live sessions.</p>
                        <p>9.2 Attendance requirements vary according to each programme.</p>
                        <p>9.3 Failure to meet the minimum attendance requirements may affect eligibility for examinations, certificates, or course completion.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>10. Intellectual Property</h4>
                        <p>10.1 All course materials, lecture recordings, videos, notes, presentations, examinations, assignments, and learning resources are the exclusive intellectual property of the Institute of Business and Accounting Studies (IBAS).</p>
                        <p>10.2 Students shall not:</p>
                        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                            <li>Download protected videos</li>
                            <li>Record lectures</li>
                            <li>Copy course materials</li>
                            <li>Share learning resources</li>
                            <li>Upload materials to social media</li>
                            <li>Distribute content to third parties</li>
                            <li>Sell or reproduce any IBAS content</li>
                        </ul>
                        <p>10.3 Any violation may result in immediate suspension, permanent termination of access, legal action, and claims for damages and compensation.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>11. Misuse of Student Accounts</h4>
                        <p>IBAS reserves the right to immediately suspend or permanently terminate any account where there is evidence of account sharing, password sharing, multiple users accessing one account, unauthorized downloads, screen recording, copying videos, unauthorized distribution of materials, or any activity considered fraudulent or harmful to IBAS. No refund will be issued following such action.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>12. Technical Requirements</h4>
                        <p>Students are responsible for ensuring they have a reliable internet connection, compatible computer/mobile device, adequate data connectivity, and updated web browsers. IBAS is not responsible for interruptions caused by power failures, internet outages, device failures, network congestion, software issues, or student-side technical problems.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>13. Communication</h4>
                        <p>Official communication will be made through email, WhatsApp Community, Student Portal, official website, or LMS. Students are responsible for regularly checking official communication channels.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>14. Student Conduct</h4>
                        <p>Students are expected to maintain professional and respectful behaviour. Disciplinary action may be taken for harassment, abuse of staff or lecturers, offensive language, threatening behaviour, disruptive conduct, academic misconduct, or breach of institute policies. No refund shall be payable following disciplinary action.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>15. Course Schedule</h4>
                        <p>IBAS reserves the right to modify lecture dates, times, schedules, lecturers, examination dates, or delivery methods due to operational, academic, technical, or unforeseen circumstances.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>16. Privacy and Data Protection</h4>
                        <p>IBAS collects and stores personal information (Full Name, NIC/Passport, DOB, Email, Phone, Course Info, Payment Records, Exam Results) in accordance with data protection laws. Information will not be disclosed to third parties without consent, except where required by law or for academic/administrative purposes.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>17. Student Achievements</h4>
                        <p>Students who receive awards or academic recognition may have their names, photos, testimonials, or achievements published by IBAS for educational, promotional, or marketing purposes unless prohibited by law.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>18. Copyright</h4>
                        <p>All educational materials provided by IBAS are protected by copyright. Any unauthorized reproduction, copying, distribution, publication, or commercial use is strictly prohibited.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>19. Limitation of Liability</h4>
                        <p>IBAS shall not be liable for direct, indirect, incidental, or consequential loss arising from technical failures, internet interruptions, delayed access, device incompatibility, force majeure events, student negligence, or third-party service failures.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>20. Complaints and Disputes</h4>
                        <p>Students must submit complaints or appeals through official communication channels. IBAS will investigate and respond within a reasonable period.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>21. Governing Law</h4>
                        <p>These Terms & Conditions shall be governed by and interpreted in accordance with the laws of the Democratic Socialist Republic of Sri Lanka. Any disputes shall be subject to the exclusive jurisdiction of the competent courts of Sri Lanka.</p>

                        <h4 style={{ color: '#03245e', marginTop: '16px', marginBottom: '8px', borderBottom: '1px solid #edf2f7', paddingBottom: '4px' }}>22. Acceptance</h4>
                        <p>By registering, making payment, accessing the IBAS website, LMS, or attending any programme offered by IBAS, the student confirms that they have read, understood, and accepted these Payment Policy and Terms & Conditions in full. Failure to comply may result in suspension or termination of services without refund.</p>
                    </div>
                </div>
            </Modal>

            <div className="confirmation-notice">
                <p><strong>Note:</strong> After verifying your payment, you will be enrolled in the course and receive a confirmation email.</p>
            </div>
        </div>
    );
};

const BuyCourse = () => {
    const allCources = useSelector(state => state.allCources.allCources);
    const [course, setCourse] = useState()
    const courseId = useParams().id
    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('card')
    const userProfile = useSelector(state => state.userProfile.userProfile);
    const usersCources = useSelector(state => state.usersCources.usersCources);
    const dispatch = useDispatch();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryInstallment = queryParams.get('installment') ? Number(queryParams.get('installment')) : null;

    const currentEnrollment = usersCources?.find(
        (uc) => (uc.courseId || uc.id) === (course?.id || courseId)
    );
    const enrollmentNextInstallment = currentEnrollment && !currentEnrollment.isPaid && Number(currentEnrollment.payedInstallments) > 0
        ? Number(currentEnrollment.payedInstallments) + 1
        : null;

    const targetInstallmentNumber = queryInstallment || enrollmentNextInstallment;

    const [userPayments, setUserPayments] = useState([]);

    useEffect(() => {
        if (userProfile?.id) {
            getPaymentsByUserId(userProfile.id).then(res => {
                if (Array.isArray(res)) setUserPayments(res);
            }).catch(e => console.error(e));
        }
    }, [userProfile]);

    const pendingSlip = React.useMemo(() => {
        if (!userPayments || userPayments.length === 0) return null;
        return userPayments.find(
            p => (p.courseId === (course?.id || courseId)) &&
                 p.paymentStatus === 'pending' &&
                 p.paymentMethod === 'Bank Deposit'
        );
    }, [userPayments, course, courseId]);

    useEffect(() => {
        if (userProfile?.id && (!usersCources || usersCources.length === 0)) {
            getUsersCourses(userProfile.id).then(res => {
                if (res) dispatch(setUsersCources(res));
            }).catch(e => console.error(e));
        }
    }, [userProfile, usersCources, dispatch]);

    const isAlumni = React.useMemo(() => {
        if (!course?.prerequisiteCourseIds || !usersCources) return false;
        try {
            const prereqIds = typeof course.prerequisiteCourseIds === 'string' 
                ? JSON.parse(course.prerequisiteCourseIds) 
                : course.prerequisiteCourseIds;
            if (!Array.isArray(prereqIds) || prereqIds.length === 0) return false;
            return usersCources.some(uc => prereqIds.includes(uc.courseId || uc.id));
        } catch (e) {
            return false;
        }
    }, [course, usersCources]);

    const isPromo = React.useMemo(() => {
        if (!course?.isPromoActive || !course?.promoPrice) return false;
        if (course.promoStartDate && course.promoEndDate) {
            const today = new Date();
            const start = new Date(course.promoStartDate);
            const end = new Date(course.promoEndDate);
            end.setHours(23, 59, 59, 999);
            return today >= start && today <= end;
        }
        return false;
    }, [course]);

    const hasValidPrereqs = React.useMemo(() => {
        if (!course?.prerequisiteCourseIds) return false;
        try {
            const ids = typeof course.prerequisiteCourseIds === 'string'
                ? JSON.parse(course.prerequisiteCourseIds)
                : course.prerequisiteCourseIds;
            return Array.isArray(ids) && ids.length > 0;
        } catch {
            return false;
        }
    }, [course]);

    useEffect(() => {
        localStorage.setItem('digital-academy-last-visit', courseId);
    }, []);

    useEffect(() => {
        // Always fetch latest course details (including installment breakdown)
        // from backend, but use Redux data as a quick initial value
        if (allCources) {
            const fromStore = allCources.find(item => item.id === courseId);
            if (fromStore) {
                setCourse(fromStore);
            }
        }
        handleGetCourse();
    }, [allCources]);

    const handleGetCourse = async () => {
        setIsLoading(true);
        try {
            const response = await getCourseById(courseId);
            if (response) {
                // New API returns a single course object with installmentBreakdown
                const courseData = Array.isArray(response) ? response[0] : response;
                setCourse(courseData);
            }
        } catch (error) {
            console.error('handleGetCourse', error);
        }
        setIsLoading(false);
    }

    return (
        <div className='buy-course'>
            <Button
                type='link'
                style={{
                    margin: '10px 0 0 0',
                    padding: 0
                }}
                onClick={() => history.goBack()}
            >
                <LeftCircleOutlined /> Go Back
            </Button>
            <h1 className='buy-title' style={{ marginTop: 5 }}>Buy Course</h1>
            <Spin spinning={isLoading}>
                <div className='course-data'>
                    <h2>Course Name: {course?.name}</h2>
                    <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: course?.description }} />
                    
                    <div style={{ margin: '15px 0' }}>
                        {isPromo ? (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#d32f2f' }}>
                                        {course?.promoPrice} LKR
                                    </span>
                                    <span style={{ fontSize: '1.15rem', color: '#8c8c8c', textDecoration: 'line-through' }}>
                                        {course?.price} LKR
                                    </span>
                                    <Tag color="red" style={{ fontSize: '0.85rem', padding: '2px 8px' }}>
                                        Promotional Offer
                                    </Tag>
                                </div>
                                {hasValidPrereqs && course?.alumniDiscount > 0 && (
                                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ color: '#52c41a', fontSize: '0.95rem', fontWeight: '600' }}>
                                            Alumni Standard Price: {course.price - course.alumniDiscount} LKR
                                        </span>
                                        {isAlumni && (
                                            <Tag color="green">Alumni Eligible</Tag>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : isAlumni && hasValidPrereqs && course?.alumniDiscount > 0 ? (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#52c41a' }}>
                                        {course.price - course.alumniDiscount} LKR
                                    </span>
                                    <span style={{ fontSize: '1.15rem', color: '#8c8c8c', textDecoration: 'line-through' }}>
                                        {course?.price} LKR
                                    </span>
                                    <Tag color="green" style={{ fontSize: '0.85rem', padding: '2px 8px' }}>
                                        Alumni Discount Applied (-{course.alumniDiscount} LKR)
                                    </Tag>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1677ff' }}>
                                        {course?.price} LKR
                                    </span>
                                    {hasValidPrereqs && course?.alumniDiscount > 0 && (
                                        <Tag color="green" style={{ fontSize: '0.85rem', padding: '2px 8px' }}>
                                            Alumni Price: {course.price - course.alumniDiscount} LKR
                                        </Tag>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <p style={{ margin: '8px 0 0 0', fontSize: '1rem', color: '#666' }}>User Name: {userProfile?.name}</p>
                </div>
            </Spin>

            {currentEnrollment && currentEnrollment.isPaid ? (
                <Alert
                    message="Already Enrolled"
                    description="You are already fully enrolled and paid for this course. You have complete access to all course modules and sessions."
                    type="success"
                    showIcon
                    action={
                        <Button type="primary" onClick={() => history.push(`/dashboard/courses/${courseId}`)}>
                            Go to Course
                        </Button>
                    }
                    style={{ marginTop: 20, marginBottom: 20, borderRadius: 8 }}
                />
            ) : (
                <>
                    {pendingSlip && (
                        <Alert
                            message="Payment Slip Under Review"
                            description={
                                <div>
                                    <p style={{ margin: 0 }}>
                                        You have submitted a bank deposit slip for this course (Amount: <strong>{pendingSlip.amount} LKR</strong>{pendingSlip.directPayTransactionId ? `, Ref: ${pendingSlip.directPayTransactionId}` : ''}) on {pendingSlip.paymentDate || 'recently'}.
                                    </p>
                                    <p style={{ margin: '6px 0 0 0', color: '#666' }}>
                                        Our team is currently verifying your transaction. Once verified, your course access will be activated automatically.
                                    </p>
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginTop: 20, marginBottom: 20, borderRadius: 8 }}
                        />
                    )}

                    <div className='payment-section'>
                        <h2>Select Payment Method</h2>
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            type="card"
                            size="large"
                            items={[
                                {
                                    key: 'card',
                                    label: (
                                        <span>
                                            <CreditCardOutlined />
                                            Card Payment
                                        </span>
                                    ),
                                    children: (
                                        <CardPaymentTab 
                                            course={course} 
                                            userProfile={userProfile} 
                                            isAlumni={isAlumni} 
                                            targetInstallmentNumber={targetInstallmentNumber} 
                                            existingEnrollment={currentEnrollment} 
                                        />
                                    ),
                                },
                                {
                                    key: 'bank',
                                    label: (
                                        <span>
                                            <BankOutlined />
                                            Bank Deposit
                                        </span>
                                    ),
                                    children: (
                                        <BankDepositTab 
                                            course={course} 
                                            userProfile={userProfile} 
                                            isAlumni={isAlumni} 
                                            targetInstallmentNumber={targetInstallmentNumber} 
                                            existingEnrollment={currentEnrollment} 
                                            pendingSlip={pendingSlip}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

export default BuyCourse